import React, { useCallback } from 'react';

interface DateFilterInputProps {
  columnKey: string;
  value: string;
  onChange: (columnKey: string, value: string) => void;
  minWidth: number;
}

/**
 * Stacked from/to date filter for date columns.
 *
 * Sends value to backend as:
 * - "2026-04-16" (only from) → >=2026-04-16
 * - "..2026-04-16" (only to) → <=2026-04-16
 * - "2026-01-01..2026-04-16" (both) → range
 * - Raw text fallback for partial dates like "2026-04"
 */
const DateFilterInput: React.FC<DateFilterInputProps> = ({ columnKey, value, onChange, minWidth }) => {
  // Parse current value into from/to parts
  const parts = value.split('..');
  let fromDate = '';
  let toDate = '';

  if (parts.length === 2) {
    fromDate = parts[0];
    toDate = parts[1];
  } else if (value.startsWith('>=')) {
    fromDate = value.slice(2);
  } else if (value.startsWith('<=')) {
    toDate = value.slice(2);
  } else if (value) {
    fromDate = value;
  }

  const buildValue = useCallback((from: string, to: string) => {
    if (from && to) return `${from}..${to}`;
    if (from) return `>=${from}`;
    if (to) return `<=${to}`;
    return '';
  }, []);

  const handleFromChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(columnKey, buildValue(e.target.value, toDate));
  }, [columnKey, toDate, onChange, buildValue]);

  const handleToChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(columnKey, buildValue(fromDate, e.target.value));
  }, [columnKey, fromDate, onChange, buildValue]);

  return (
    <div className="date-filter-container" style={{ minWidth: `${minWidth}px` }}>
      <div className="date-filter-row">
        <span className="date-filter-label">Fra</span>
        <input
          type="date"
          value={fromDate}
          onChange={handleFromChange}
          className="date-filter-input"
        />
      </div>
      <div className="date-filter-row">
        <span className="date-filter-label">Til</span>
        <input
          type="date"
          value={toDate}
          onChange={handleToChange}
          className="date-filter-input"
        />
      </div>
    </div>
  );
};

export default DateFilterInput;
